import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import { toast } from 'react-toastify'
import { uploadGroupReceipts, type AnalysisMode, type GroupDetail, type Receipt } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { useCapabilities } from '../../hooks/useCapabilities'
import { receiptQueueQueryKey } from '../../hooks/useReceipts'
import { nazevRozpoznavani, useZpracovaniUctenek } from '../../hooks/useZpracovaniUctenek'
import { MAX_FOTEK, naNahrani, pocetFotek, pridejFotky, ReceiptUploadGrouping, type PripravenaUctenka } from './ReceiptUploadGrouping'

export type ReceiptUploadModalProps = {
    show: boolean
    group: GroupDetail
    /** Fotky vybrané ještě před otevřením dialogu. */
    files: File[]
    onClose: () => void
}

/** Co se s nahranými účtenkami děje dál - podle toho, kdo je čte. */
function hlaskaPoNahrani(rezim: AnalysisMode, nastroj: string | undefined): string {
    const kde = 'Najdete je v sekci Ke kontrole nahoře v historii.'
    switch (rezim) {
        case 'MCP':
            return `Účtenky čekají na AI asistenta — požádejte ho o zpracování účtenek ve SplitUpu. ${kde}`
        case 'BOTH':
            return `Účtenky čte ${nastroj} a čekají i na AI asistenta. ${kde}`
        default:
            return nastroj ? `Účtenky čte ${nastroj}. ${kde}` : `Účtenky jsou nahrané, údaje doplníte ručně. ${kde}`
    }
}

/**
 * Nahrání účtenek do skupiny: seskupení fotek a volba, kdo je přečte.
 *
 * Po nahrání se dialog zavře - na nic se nečeká. Rozpoznávání běží na serveru
 * a na AI asistenta se čeká na pozadí; účtenky se mezitím objeví v sekci
 * Ke kontrole, odkud z nich po rozebrání položek vznikne útrata.
 */
export function ReceiptUploadModal({ show, group, files, onClose }: ReceiptUploadModalProps) {
    const queryClient = useQueryClient()
    const { data: capabilities } = useCapabilities()
    const zpracovani = useZpracovaniUctenek()
    const souborInput = useRef<HTMLInputElement>(null)

    const [pripravene, setPripravene] = useState<PripravenaUctenka[]>(() => pridejFotky([], files.slice(0, MAX_FOTEK)))
    const [nahravam, setNahravam] = useState(false)

    const pridej = (dalsi: File[]) => {
        const volno = Math.max(MAX_FOTEK - pocetFotek(pripravene), 0)
        if (dalsi.length > volno) {
            toast.warning(`Najednou lze nahrát nejvýš ${MAX_FOTEK} fotek, zbytek nahrajte zvlášť.`)
        }
        setPripravene(pridejFotky(pripravene, dalsi.slice(0, volno)))
    }

    const nahraj = async () => {
        const { files: soubory, receiptIndexes } = naNahrani(pripravene)
        const { analysisMode } = zpracovani

        setNahravam(true)
        try {
            const nahrane = await actionApiCall(() =>
                uploadGroupReceipts({ path: { groupId: group.id }, body: { files: soubory, analysisMode, receiptIndexes } }),
            )
            if (!nahrane) {
                return
            }

            // Nové účtenky jsou v sekci Ke kontrole vidět hned, ne až po dalším dotazu.
            queryClient.setQueryData<Receipt[]>(receiptQueueQueryKey(group.id), (predchozi) => [...(predchozi ?? []), ...nahrane])
            const nastroj = capabilities?.receiptAnalysis ? nazevRozpoznavani(capabilities.receiptAnalysisProvider) : undefined
            toast.success(hlaskaPoNahrani(analysisMode, nastroj))
            onClose()
        } finally {
            setNahravam(false)
        }
    }

    const pocet = pripravene.length

    return (
        <Modal show={show} onHide={onClose} centered scrollable size="lg">
            <Modal.Header closeButton>
                <Modal.Title as="h2" className="h5">Příprava účtenek</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <input
                    ref={souborInput}
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    hidden
                    data-testid="dalsi-fotky"
                    onChange={(event) => {
                        pridej([...(event.target.files ?? [])])
                        event.target.value = ''
                    }}
                />
                <ReceiptUploadGrouping
                    uctenky={pripravene}
                    onChange={setPripravene}
                    onAddPhotos={() => souborInput.current?.click()}
                    maxFotek={MAX_FOTEK}
                    zpracovani={zpracovani}
                    analysisAvailable={capabilities?.receiptAnalysis ?? false}
                    analysisProvider={capabilities?.receiptAnalysisProvider}
                    disabled={nahravam}
                />
                <div className="small text-secondary">
                    Účtenky se zpracují na pozadí — aplikaci můžete klidně zavřít. Až budou hotové, rozeberete v nich
                    položky a útratu potvrdíte.
                </div>
            </Modal.Body>

            <Modal.Footer className="justify-content-between">
                <Button variant="outline-secondary" className="su-tappable" disabled={nahravam} onClick={onClose}>
                    Zrušit
                </Button>
                <Button className="su-tappable" disabled={nahravam || pocet === 0} onClick={() => void nahraj()}>
                    {nahravam && <Spinner animation="border" size="sm" className="me-2" />}
                    Nahrát {pocet} {pocet === 1 ? 'účtenku' : pocet >= 2 && pocet <= 4 ? 'účtenky' : 'účtenek'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
